import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Card,
    HStack,
    Spinner, Text, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import { Alert, ThemeTokens } from '@acp/core-ui';
import { AccessIssueSolutionLabel, PortfolioPropertyTag, PropertyTenancies } from '@acp/shared';
import { useMemo } from 'react';
import { TenancyCard } from './tenancy-card';
import { AddTenancyModal } from './modals/add-tenancy-modal';
import { useProperty, usePropertyCancelled } from '../../property';
import { useTenancy } from '../hooks';
import { TenancyActionsMenuType } from './tenancy-actions';
import { useTenancyForm } from '../hooks/use-tenancy-form';
import { useConfirmSinglePropertyMutation } from '../../action-required/update/components/use-confirm-single-property-details-mutation';
import { IntegratedPropertyDialog } from '../../property/components/integrated-property-dialog';

function UpcomingLeaseAgreementsAccordion({
    propertyId,
    isPropertyIntegrated,
    upcomingtenancies,
}: {
    propertyId: number;
    isPropertyIntegrated: boolean;
    upcomingtenancies: PropertyTenancies['allUpcoming'];
}) {
    return (
        <Accordion as={VStack} allowMultiple gap={5} alignItems="stretch" variant="bootstrap">
            <AccordionItem border="none">
                <AccordionButton py={3} minH={16}>
                    <VStack flexGrow="1" alignItems="start" w="100%" gap={0}>
                        <Text fontWeight="medium">Upcoming lease agreements</Text>
                    </VStack>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <VStack align="left" gap={6}>
                        {
                            upcomingtenancies.map((tenancy) => (
                                <Card
                                    key={tenancy.tenancyId}
                                    borderColor="muted.50"
                                    borderWidth={1}
                                    alignSelf="flex-start"
                                    bg="white"
                                    variant="filled"
                                    w="full"
                                    shadow="sm"
                                    p={5}
                                >
                                    <TenancyCard
                                        propertyId={propertyId}
                                        tenancy={tenancy}
                                        hideTenancyActions={isPropertyIntegrated}
                                        showTenancyActionsMenuAs={TenancyActionsMenuType.BUTTON}
                                    />
                                </Card>
                            ))
                        }
                    </VStack>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );
}

export function TenancyForm({
    propertyId,
    onTenancyConfirmed,
    formType,
    confirmButtonText,
    showUpcomingLeaseAgreements = false,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onTenancyConfirmed?: () => void;
    formType?: AccessIssueSolutionLabel;
    showUpcomingLeaseAgreements?: boolean;
}) {
    const addTenancyModalDisclosure = useDisclosure();
    const toast = useToast();
    const tenanciesQuery = useTenancy(propertyId);
    const propertyQuery = useProperty(propertyId);
    const isPropertyCancelled = usePropertyCancelled(propertyId);

    const tags = propertyQuery.data?.tags ?? [];
    const isPropertyIntegrated = tags.includes(PortfolioPropertyTag.Integrated);

    const currentTenancy = useMemo(() => {
        if (tenanciesQuery.data !== undefined) {
            return tenanciesQuery.data.active;
        }

        return null;
    }, [tenanciesQuery]);

    const upcomingTenancies = useMemo(() => {
        if (tenanciesQuery.data !== undefined) {
            return tenanciesQuery.data.allUpcoming;
        }

        return [];
    }, [tenanciesQuery]);

    const { confirmButtonLabel } = useTenancyForm(currentTenancy, formType);

    const mutation = useConfirmSinglePropertyMutation(propertyId, {
        onSuccess: () => {
            toast({
                title: 'Tenancy details confirmed',
                description: 'Tenancy details confirmed successfully',
                status: 'success',
            });
            onTenancyConfirmed?.();
        },
        onError: () => {
            toast({
                title: 'Failed to confirm tenancy details',
                description: 'An error occurred confirming tenancy details.',
                status: 'error',
            });
        },
    });

    const handleTenancyConfirmed = () => {
        mutation.mutate();
    };

    if (
        (propertyQuery.data === undefined && propertyQuery.isLoading)
        || (tenanciesQuery.data === undefined && tenanciesQuery.isLoading)
    ) {
        return <Spinner size="sm" color="muted.400" />;
    }

    if (isPropertyCancelled) {
        // Shouldn't happen, but here just in case.
        return (
            <Alert title="This property has been cancelled." status="error">
                Please contact Detector Inspector for further assistance.
            </Alert>
        );
    }

    return (
        <Box>
            <VStack align="left" w="full">
                <VStack align="left" spacing={6}>
                    {isPropertyIntegrated && (
                        <IntegratedPropertyDialog
                            title="This property's tenancy data is synced with your property management software."
                        />
                    )}

                    {currentTenancy === null && (
                        // No current tenancy.
                        <Alert title="There is no current tenancy for this property" status="info">
                            Current tenancy details are required in order for us to
                            complete an upcoming service.
                        </Alert>
                    )}

                    {currentTenancy !== null && (
                        <>
                            {currentTenancy.occupants.length === 0 && (
                                // Has a current tenancy, but no occupants.
                                <Alert title="This property is currently unoccupied" status="info">
                                    <Text>
                                        At least one occupant must be included in this
                                        tenancy in order for us to complete an upcoming
                                        service.
                                    </Text>
                                </Alert>
                            )}

                            <Text fontWeight="bold">Current lease agreement</Text>
                            <TenancyCard
                                propertyId={propertyId}
                                tenancy={currentTenancy}
                                hideTenancyActions={isPropertyIntegrated}
                                showTenancyActionsMenuAs={TenancyActionsMenuType.BUTTON}
                            />
                        </>
                    )}

                    {showUpcomingLeaseAgreements
                    && upcomingTenancies !== null
                    && upcomingTenancies.length > 0
                    && (
                        <UpcomingLeaseAgreementsAccordion
                            propertyId={propertyId}
                            isPropertyIntegrated={isPropertyIntegrated}
                            upcomingtenancies={upcomingTenancies}
                        />
                    )}
                </VStack>
            </VStack>
            <HStack pt={6} justify="end">
                {isPropertyIntegrated === false && !mutation.isPending && (
                    <Button
                        alignSelf="end"
                        w="fit-content"
                        borderColor={ThemeTokens.Defaults.borderColor}
                        variant="outline"
                        colorScheme="muted.700"
                        size="md"
                        onClick={addTenancyModalDisclosure.onOpen}
                    >
                        Add tenancy
                    </Button>
                )}
                {onTenancyConfirmed !== undefined && (
                    <Button
                        alignSelf="end"
                        w="fit-content"
                        variant="solid"
                        colorScheme="secondary"
                        size="md"
                        isLoading={mutation.isPending}
                        onClick={handleTenancyConfirmed}
                    >
                        {confirmButtonText ?? confirmButtonLabel}
                    </Button>
                )}
            </HStack>

            {addTenancyModalDisclosure.isOpen && (
                <AddTenancyModal
                    propertyId={propertyId}
                    isOpen={addTenancyModalDisclosure.isOpen}
                    onClose={addTenancyModalDisclosure.onClose}
                />
            )}
        </Box>
    );
}
